import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { chefPortraitFor } from "@/lib/chefAvatar";
import { Plus, Pencil, Trash2, Loader2, Users, ArrowLeft, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", title: "", restaurant: "", bio: "", photo_url: "", featured: false };

function resolvePhoto(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Contributors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contributors");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this contributor? Any posts already attributed to them will lose the byline.")) return;
    await api.delete(`/contributors/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="contributors-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Users className="w-3 h-3" /> Executive Chef Contributors</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Guest Chefs</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            Feature guest articles from top industry names. Every published post can carry a chef's byline, photo and restaurant title in the "About the author" card at the bottom.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="contributors-view-press"><Link to="/press" target="_blank" className="gap-2"><ExternalLink className="w-3.5 h-3.5" /> View on /press</Link></Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="gap-2" data-testid="contributor-new-btn"><Plus className="w-4 h-4" /> Add contributor</Button>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl glass-card p-12 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm mb-1">No contributors yet.</p>
          <p className="text-xs text-muted-foreground mb-4">Ask an Executive Chef you know. "I'm launching a specialist tool. Would you write a guest piece?" is a solid opener.</p>
          <Button onClick={() => setDialogOpen(true)} className="gap-2"><Plus className="w-4 h-4" /> Add your first</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contributors-grid">
          {items.map((c) => (
            <div key={c.id} className="glass-card rounded-2xl p-5 flex gap-4" data-testid={`contributor-${c.id}`}>
              {c.photo_url ? (
                <img src={resolvePhoto(c.photo_url)} alt={c.name} className="w-16 h-16 rounded-full object-cover shrink-0" />
              ) : (
                <img src={chefPortraitFor(c)} alt={c.name} loading="lazy" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ filter: "saturate(0.9) contrast(1.05)" }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <div className="font-display text-lg truncate">{c.name}</div>
                  {c.featured && <Star className="w-3 h-3 fill-primary text-primary" />}
                </div>
                <div className="text-xs gold truncate">{c.title}</div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{c.bio}</p>
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(c); setDialogOpen(true); }} className="h-7 gap-1 text-xs" data-testid={`contributor-edit-${c.id}`}><Pencil className="w-3 h-3" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="h-7" data-testid={`contributor-delete-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContributorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
}

function ContributorDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.title.trim()) {
      toast.error("Name and title are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/contributors/${initial.id}`, form);
      else await api.post("/contributors", form);
      toast.success(isEdit ? "Updated" : "Contributor added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="contributor-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit contributor" : "New contributor"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Antoine Girard" data-testid="contributor-name" /></div>
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Executive Chef, Le Bernardin" data-testid="contributor-title" /></div>
          <div><Label>Restaurant (optional)</Label><Input value={form.restaurant || ""} onChange={(e) => set("restaurant", e.target.value)} placeholder="Le Bernardin" data-testid="contributor-restaurant" /></div>
          <div><Label>Bio</Label><Textarea rows={4} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Two or three sentences about the chef." data-testid="contributor-bio" /></div>
          <ImageUpload value={form.photo_url} onChange={(v) => set("photo_url", v)} label="Photo (headshot)" testId="contributor-photo" />
          <div className="flex items-center justify-between border border-white/10 rounded p-3">
            <div><div className="text-sm font-medium">Featured on /press</div><div className="text-xs text-muted-foreground">Featured chefs appear at the top.</div></div>
            <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="contributor-featured" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="contributor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact reusable author-bio card. Used on public blog post detail pages.
export function AuthorBioCard({ contributor }) {
  if (!contributor) return null;
  const archiveLink = contributor.slug ? `/authors/${contributor.slug}` : null;
  return (
    <aside className="glass-card rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row gap-4 items-start mt-10" data-testid="author-bio">
      {contributor.photo_url ? (
        <img src={resolvePhoto(contributor.photo_url)} alt={contributor.name} className="w-20 h-20 rounded-full object-cover shrink-0" />
      ) : (
        <img src={chefPortraitFor(contributor)} alt={contributor.name} loading="lazy" className="w-20 h-20 rounded-full object-cover shrink-0" style={{ filter: "saturate(0.9) contrast(1.05)" }} />
      )}
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow gold">About the author</div>
        <div className="font-display text-xl leading-tight mt-1" data-testid="author-bio-name">{contributor.name}</div>
        <div className="text-sm gold mt-0.5" data-testid="author-bio-title">{contributor.title}</div>
        {contributor.bio && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{contributor.bio}</p>}
        {archiveLink && (
          <a href={archiveLink} className="inline-block mt-3 text-xs gold hover:underline" data-testid="author-bio-archive-link">
            More articles by {contributor.name.split(" ")[0]} →
          </a>
        )}
      </div>
    </aside>
  );
}
